import React, { useMemo } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { Button, Stack, Typography } from '@mui/material';

import { FloorPolyline3D } from '@paragon/contract-library';
import { calculateFloorPolyline2DShape } from '@paragon/layout';

import { beginRedefiningActiveFeaturePatternContainerGeometry } from '../../../store/actions/layout/featurePatternContainerActions';
import { getAllBearingEnvelopesDictionary } from '../../../store/selectors/layoutDataSelectors';
import { getPickingObjectInfo } from '../../../store/selectors/pickingSelectors';
import { clearPickingObject } from '../../../store/slices/applicationSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { LabeledBox } from '../../interface/subpanels/properties/LabeledBox';

interface Props {
  readonly value: FloorPolyline3D;
  readonly disabled?: boolean;
}

export function FeaturePatternContainerGeometryField({ value, disabled }: Props) {
  const dispatch = useAppDispatch();

  // When editing an existing container, this behaves like most other point picking inputs with full start/stop control.
  // In insertion mode, picking is forced on (and manually signifying an end to picking is not required), so we can
  //   have a simpler interface.
  const insertionMode = useAppSelector(
    (state) => state.application.insertingObjectInfo?.type === 'featurePatternContainer',
  );

  const isManuallyPickingBoundary = useAppSelector((state) => {
    const pickingInfo = getPickingObjectInfo(state);
    return pickingInfo?.type === 'pickingLayoutLine' && pickingInfo.target === 'feature-pattern-container-boundary';
  });

  const bearingEnvelopes = useAppSelector(getAllBearingEnvelopesDictionary);
  const fullyBounded = useMemo(
    () => calculateFloorPolyline2DShape(value, bearingEnvelopes) != null,
    [value, bearingEnvelopes],
  );

  const editModePickButtonText = isManuallyPickingBoundary
    ? fullyBounded
      ? 'Finish Picking'
      : 'Stop Picking'
    : value.geometry.length === 0
      ? 'Pick'
      : 'Pick Again';

  return (
    <LabeledBox title="Shape" contentSx={{ padding: 1 }}>
      <Stack direction="row" sx={{ alignItems: 'center' }} className="clickable-during-picking">
        {insertionMode ? (
          <Typography sx={{ display: 'flex', alignItems: 'center' }}>
            {fullyBounded ? (
              <>
                <CheckBoxIcon color="success" /> Valid shape ({value.geometry.length} segments)
              </>
            ) : (
              'Pick boundary segments...'
            )}
          </Typography>
        ) : (
          <Typography>{value.geometry.length} boundary segments.</Typography>
        )}

        <Button
          sx={{ marginLeft: 'auto' }}
          variant={!insertionMode && isManuallyPickingBoundary ? 'contained' : 'outlined'}
          onClick={() => {
            if (!insertionMode && isManuallyPickingBoundary) {
              dispatch(clearPickingObject());
            } else {
              dispatch(beginRedefiningActiveFeaturePatternContainerGeometry());
            }
          }}
          disabled={disabled || (insertionMode && value.geometry.length === 0)}
        >
          {insertionMode ? 'Start Over' : editModePickButtonText}
        </Button>
      </Stack>
    </LabeledBox>
  );
}
